/**
 * e2eCrypto.js — client-side (zero-knowledge) encryption for send.intake.dental.
 *
 * Format ('e2e-aes-gcm-chunked-v1'): each file is a sequence of frames
 *   [4-byte BE ciphertext length][12-byte IV][ciphertext incl. 16-byte GCM tag]
 * encrypted with a random per-transfer 256-bit AES-GCM key in 8 MiB plaintext
 * chunks (constant memory for GB files). The key travels either in the URL
 * fragment (#k=..., never sent to any server) or PIN-wrapped
 * (PBKDF2-SHA256 600k → AES-GCM) stored server-side for emailed links.
 *
 * Isomorphic: uses globalThis.crypto.subtle — works in browsers and Node ≥20,
 * so the exact code that ships is what the test harness exercises.
 */

const subtle = globalThis.crypto.subtle;
export const CHUNK_SIZE = 8 * 1024 * 1024;           // 8 MiB plaintext per frame
export const FRAME_OVERHEAD = 4 + 12 + 16;           // len + IV + GCM tag
export const ALGORITHM = 'e2e-aes-gcm-chunked-v1';
const PBKDF2_ITERATIONS = 600_000;

// ── base64url helpers ───────────────────────────────────────────────────────
export function toB64url(bytes) {
  let s = '';
  const b = new Uint8Array(bytes);
  for (let i = 0; i < b.length; i += 0x8000) s += String.fromCharCode(...b.subarray(i, i + 0x8000));
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
export function fromB64url(s) {
  const bin = atob(String(s).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// ── keys ────────────────────────────────────────────────────────────────────
export async function generateTransferKey() {
  const raw = crypto.getRandomValues(new Uint8Array(32));
  const key = await subtle.importKey('raw', raw, 'AES-GCM', false, ['encrypt', 'decrypt']);
  return { key, rawB64url: toB64url(raw) };
}

export async function importTransferKey(rawB64url) {
  return subtle.importKey('raw', fromB64url(rawB64url), 'AES-GCM', false, ['encrypt', 'decrypt']);
}

// PIN-wrap for emailed links (the email can't carry a fragment we never see).
// NOTE: a 4-digit PIN is weak against an offline attacker who also has the
// wrapped blob — the fragment link is the true zero-knowledge path; this one
// is "encrypted, key protected by your PIN".
export async function wrapKeyWithPin(rawB64url, pin) {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const base = await subtle.importKey('raw', new TextEncoder().encode(String(pin)), 'PBKDF2', false, ['deriveKey']);
  const kek = await subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    base, { name: 'AES-GCM', length: 256 }, false, ['encrypt']);
  const ct = await subtle.encrypt({ name: 'AES-GCM', iv }, kek, fromB64url(rawB64url));
  return {
    v: 1, alg: ALGORITHM, kdf: 'pbkdf2-sha256', iterations: PBKDF2_ITERATIONS,
    salt: toB64url(salt), iv: toB64url(iv), ct: toB64url(ct)
  };
}

export async function unwrapKeyWithPin(wrapped, pin) {
  const base = await subtle.importKey('raw', new TextEncoder().encode(String(pin)), 'PBKDF2', false, ['deriveKey']);
  const kek = await subtle.deriveKey(
    { name: 'PBKDF2', salt: fromB64url(wrapped.salt), iterations: wrapped.iterations || PBKDF2_ITERATIONS, hash: 'SHA-256' },
    base, { name: 'AES-GCM', length: 256 }, false, ['decrypt']);
  const raw = await subtle.decrypt({ name: 'AES-GCM', iv: fromB64url(wrapped.iv) }, kek, fromB64url(wrapped.ct));
  return { key: await subtle.importKey('raw', raw, 'AES-GCM', false, ['encrypt', 'decrypt']), rawB64url: toB64url(raw) };
}

// ── size math (server needs the exact ciphertext size up front) ─────────────
export function ciphertextSize(plainSize) {
  const chunks = Math.max(1, Math.ceil(plainSize / CHUNK_SIZE));
  return plainSize + chunks * FRAME_OVERHEAD;
}

// ── encryption: async generator of ciphertext Blobs sized to partSize ───────
export async function* encryptToParts(file, key, partSize) {
  let buffered = [];
  let bufferedBytes = 0;
  for (let off = 0; off < file.size || off === 0; off += CHUNK_SIZE) {
    const plain = await file.slice(off, off + CHUNK_SIZE).arrayBuffer();
    if (plain.byteLength === 0 && off > 0) break;
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ct = new Uint8Array(await subtle.encrypt({ name: 'AES-GCM', iv }, key, plain));
    const head = new Uint8Array(4 + 12);
    new DataView(head.buffer).setUint32(0, ct.length, false);
    head.set(iv, 4);
    buffered.push(head, ct);
    bufferedBytes += head.length + ct.length;
    while (bufferedBytes >= partSize) {
      const all = concat(buffered);
      yield new Blob([all.subarray(0, partSize)]);
      const rest = all.subarray(partSize);
      buffered = rest.length ? [rest] : [];
      bufferedBytes = rest.length;
    }
    if (file.size === 0) break;
  }
  if (bufferedBytes > 0) yield new Blob(concat(buffered).length ? [concat(buffered)] : []);
}

function concat(arrs) {
  const total = arrs.reduce((a, x) => a + x.length, 0);
  const out = new Uint8Array(total);
  let o = 0;
  for (const a of arrs) { out.set(a, o); o += a.length; }
  return out;
}

// ── decryption: framed ciphertext (ArrayBuffer/Uint8Array) → Blob ───────────
export async function decryptFramed(cipherBytes, key) {
  const buf = cipherBytes instanceof Uint8Array ? cipherBytes : new Uint8Array(cipherBytes);
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const parts = [];
  let off = 0;
  while (off < buf.length) {
    if (off + 16 > buf.length) throw new Error('Truncated e2e frame header');
    const ctLen = view.getUint32(off, false);
    const iv = buf.subarray(off + 4, off + 16);
    const ct = buf.subarray(off + 16, off + 16 + ctLen);
    if (ct.length !== ctLen) throw new Error('Truncated e2e frame body');
    parts.push(new Uint8Array(await subtle.decrypt({ name: 'AES-GCM', iv }, key, ct)));
    off += 16 + ctLen;
  }
  return new Blob(parts);
}
